import { atom } from 'jotai';
import type { BucketStringT } from '../analytics/types/performance';
import { COMPLEXITY_THRESHOLDS, FRAME_POINT_COUNT_THRESHOLDS, TOTAL_POINT_COUNT_THRESHOLDS } from '../constants/performance';
import { bucketize } from '../utils/bucketize';
import { getPointCountFromGeometry } from '../utils/get-point-count-from-geometry';
import { annotationMetadataAtom, currentFrameLoadedAnnotationsAtom, frameCountAtom, loadedAnnotationFramesAtom } from './annotation-atoms';

/**
 * Total point count across all frames. This atom will be in-accurate in
 * windowed mode as it only calculates points in currently loaded frames.
 */
export const totalPointCountAtom = atom<number>(get => {
  const allFrames = get(loadedAnnotationFramesAtom);
  const metadata = get(annotationMetadataAtom);
  let count = 0;
  for (const frameMap of allFrames.values()) {
    for (const [annotationId, payload] of frameMap) {
      const annotationType = metadata.get(annotationId)?.annotationType;
      if (annotationType) {
        count += getPointCountFromGeometry(payload.geometry, annotationType);
      }
    }
  }
  return count;
});

/**
 * Derived atom computing point count for the current frame only.
 */
export const framePointCountAtom = atom<number>(get => {
  const frameMap = get(currentFrameLoadedAnnotationsAtom);
  const metadata = get(annotationMetadataAtom);
  let count = 0;
  for (const [annotationId, payload] of frameMap) {
    const annotationType = metadata.get(annotationId)?.annotationType;
    if (annotationType) {
      count += getPointCountFromGeometry(payload.geometry, annotationType);
    }
  }
  return count;
});

/**
 * Derived atom returning a bucketed complexity string based on
 * annotationCount * frameCount. Used as an M3 metric tag.
 */
export const complexityBucketAtom = atom<BucketStringT>(get => {
  const metadata = get(annotationMetadataAtom);
  const frameCount = get(frameCountAtom);
  let annotationFramePairs = 0;
  for (const m of metadata.values()) {
    for (const span of m.spans) {
      annotationFramePairs += span.end - span.start + 1;
    }
  }
  return bucketize(annotationFramePairs * frameCount, [...COMPLEXITY_THRESHOLDS]);
});

/**
 * Bucketed total point count across all frames.
 */
export const totalPointCountBucketAtom = atom<BucketStringT>(get => {
  const totalPointCount = get(totalPointCountAtom);
  return bucketize(totalPointCount, [...TOTAL_POINT_COUNT_THRESHOLDS]);
});

/**
 * Bucketed point count for the current frame.
 */
export const framePointCountBucketAtom = atom<BucketStringT>(get => {
  const framePointCount = get(framePointCountAtom);
  return bucketize(framePointCount, [...FRAME_POINT_COUNT_THRESHOLDS]);
});